import React, { useEffect, useState } from 'react';
import { Grid, Paper, OutlinedInput, InputLabel, Button, Box, Typography, IconButton, Collapse, LinearProgress, MenuItem, Select } from '@mui/material';
import SpeedDial from '@mui/material/SpeedDial';
import SpeedDialIcon from '@mui/material/SpeedDialIcon';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import FilterListIcon from '@mui/icons-material/FilterList';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import EditIcon from '@mui/icons-material/Edit';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import PageHeader from '../../../components/page-header';
import { dataGridLocaleConfig } from '../../../config/data-grid-locale';
import { useParlamentarianSearchController } from './context/parlamentarian-search-controller';
import { useParlamentarianSearchState } from './context/parlamentarian-search-context';
import ParlamentarianAppService from '../../../application/platform/sge/parlamentarian/parlamentarian-app-service';
import { IParlamentarianPagedResponse } from '../../../model/sge/parlamentarian/response/parlamentarian-paged-response';
import { TIPO_PARLAMENTAR } from './parlamentarian-create-edit-component';

const TIPO_MAP: Record<number, string> = {
    1: 'Deputado Estadual',
    2: 'Deputado Federal',
    3: 'Senador',
};

export function ParlamentarianSearchComponent() {
    const [filtersOpen, setFiltersOpen] = useState(false);
    const [rows, setRows] = useState<IParlamentarianPagedResponse[]>([]);
    const [loading, setLoading] = useState(false);

    const { handleOnChange, handleSearch, handleClearFilters, navigateToCreate, navigateToEdit } = useParlamentarianSearchController();
    const { parameters, searchTrigger } = useParlamentarianSearchState();

    useEffect(() => {
        setLoading(true);
        ParlamentarianAppService.list(parameters)
            .then((result) => setRows(result.items))
            .finally(() => setLoading(false));
    }, [searchTrigger]);

    const columns: GridColDef[] = [
        { field: 'id', headerName: 'ID', width: 80 },
        { field: 'personName', headerName: 'Nome', flex: 1, minWidth: 200 },
        {
            field: 'type', headerName: 'Tipo', width: 160,
            valueFormatter: (params) => TIPO_MAP[params.value as number] ?? params.value ?? ''
        },
        { field: 'politicalPartyAbbreviation', headerName: 'Partido', width: 100 },
        { field: 'isActive', headerName: 'Ativo', width: 80, type: 'boolean' },
        {
            field: 'actions', headerName: 'Ações', width: 80, sortable: false,
            renderCell: (params) => (
                <IconButton size="small" onClick={() => navigateToEdit(params.row.id)}>
                    <EditIcon fontSize="small" />
                </IconButton>
            )
        }
    ];

    return (
        <React.Fragment>
            <Grid item xs={12}>
                <PageHeader
                    title="Parlamentares"
                    subtitle="Gerencie os parlamentares cadastrados"
                    breadcrumbs={[{ label: 'Parlamentares' }]}
                />
            </Grid>
            <Grid item xs={12}>
                <Paper sx={{ p: 2 }}>
                    <Box
                        sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer', borderRadius: 1, p: 1, ml: -1, mr: -1, mb: filtersOpen ? 2 : 0, '&:hover': { backgroundColor: 'action.hover' } }}
                        onClick={() => setFiltersOpen(!filtersOpen)}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <FilterListIcon color="action" />
                            <Typography variant="h6">Filtros</Typography>
                        </Box>
                        <IconButton size="small" sx={{ pointerEvents: 'none' }}>
                            {filtersOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                        </IconButton>
                    </Box>
                    <Collapse in={filtersOpen}>
                        <Grid container spacing={1.5}>
                            <Grid item xs={12} md={8}>
                                <InputLabel>Nome</InputLabel>
                                <OutlinedInput fullWidth name="input-name" value={parameters.name || ''} onChange={handleOnChange} />
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <InputLabel>Tipo</InputLabel>
                                <Select fullWidth name="select-type" value={parameters.type ?? ''} onChange={handleOnChange}>
                                    <MenuItem value=""><em>Todos</em></MenuItem>
                                    {TIPO_PARLAMENTAR.map((t) => (
                                        <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>
                                    ))}
                                </Select>
                            </Grid>
                            <Grid item xs={12}>
                                <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                                    <Button variant="outlined" color="secondary" startIcon={<ClearIcon />} onClick={handleClearFilters}>Limpar</Button>
                                    <Button variant="contained" startIcon={<SearchIcon />} onClick={handleSearch}>Consultar</Button>
                                </Box>
                            </Grid>
                        </Grid>
                    </Collapse>
                </Paper>
            </Grid>
            <Grid item xs={12}>
                <Box sx={{ position: 'relative', pb: '28px' }}>
                    <Paper sx={{ p: 2, height: '500px', display: 'flex', flexDirection: 'column' }}>
                        <DataGrid
                            rows={rows}
                            columns={columns}
                            loading={loading}
                            columnVisibilityModel={{ id: false }}
                            initialState={{ pagination: { paginationModel: { pageSize: 100, page: 0 } } }}
                            sx={{ flex: 1 }}
                            disableRowSelectionOnClick
                            components={{ LoadingOverlay: LinearProgress }}
                            localeText={dataGridLocaleConfig}
                        />
                    </Paper>
                    <SpeedDial ariaLabel="Novo Parlamentar" sx={{ position: 'absolute', bottom: 0, right: 16 }} onClick={navigateToCreate} icon={<SpeedDialIcon />} />
                </Box>
            </Grid>
        </React.Fragment>
    );
}
